/**
 * Quantity offer picker for the product form.
 *
 * Prices are rendered by Liquid. This element keeps the product form's quantity in step with
 * the chosen offer, so the store's automatic discounts apply in the cart.
 *
 * With `data-per-item` (products with variants), the chosen offer shows one line per item, each
 * with its own variant menu. The first line drives the product's own variant picker (hidden on
 * the page), so the gallery follows it. Items in different variants are added in one request.
 */
class VolumePicker extends HTMLElement {
  /** @type {number[]} Variant id chosen for each item line, kept when the quantity changes. */
  #chosen = [];

  /** @type {Map<number, {id: number, title: string, available: boolean, option1: string, image: string}>} */
  #variants = new Map();

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {Set<number>} Item lines the customer changed by hand; the swatches leave them alone. */
  #touched = new Set();

  connectedCallback() {
    // A cached page can outlive the sale: drop the sale styling once its end date has passed.
    const promoEnd = this.dataset.promoEnd ? Date.parse(this.dataset.promoEnd) : NaN;
    if (!Number.isNaN(promoEnd) && Date.now() > promoEnd) this.removeAttribute('data-promo');

    this.addEventListener('change', this.#onChange);
    this.#abort = new AbortController();

    if (this.hasAttribute('data-per-item')) {
      this.#readVariants();
      this.querySelectorAll('[data-volume-picker-swatch]').forEach((swatch) => {
        swatch.addEventListener('click', () => this.#pickSwatch(Number(swatch.getAttribute('data-volume-picker-swatch'))));
      });
      // The theme handles form submits with a capturing listener on document, so this one sits on
      // window, which the capture phase reaches first.
      window.addEventListener('submit', this.#onSubmit, { capture: true, signal: this.#abort.signal });
    }
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#abort?.abort();
  }

  get #scope() {
    return this.closest('.shopify-section') ?? document;
  }

  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.#scope.querySelector('form[action*="/cart/add"]'));
  }

  get #quantityInput() {
    const form = this.#form;
    if (!form) return null;

    let input = form.querySelector('input[name="quantity"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      form.append(input);
    }
    return /** @type {HTMLInputElement} */ (input);
  }

  get #currentVariantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('input[name="id"]'));
    return Number(input?.value) || this.#variants.keys().next().value || 0;
  }

  #readVariants() {
    const script = this.querySelector('script[data-volume-picker-variants]');
    if (!script?.textContent) return;

    for (const variant of JSON.parse(script.textContent)) {
      const src = variant.featured_image?.src ?? '';
      this.#variants.set(variant.id, {
        id: variant.id,
        title: variant.title,
        available: variant.available,
        option1: variant.option1,
        image: src ? `${src}${src.includes('?') ? '&' : '?'}width=96` : '',
      });
    }
  }

  #onChange = (/** @type {Event} */ event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.hasAttribute('data-volume-picker-item')) return;

    if (target instanceof HTMLSelectElement) {
      const radio = target.closest('.volume-picker__option')?.querySelector('input[type="radio"]');
      if (radio instanceof HTMLInputElement) radio.checked = true;
    }
    this.#sync();
  };

  get #checkedOption() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.volume-picker__option:has(input[type="radio"]:checked)'));
  }

  #sync() {
    const option = this.#checkedOption;
    if (!option) return;

    const select = /** @type {HTMLSelectElement | null} */ (option.querySelector('select.volume-picker__select'));
    const choice = select ? select.selectedOptions[0] : null;
    const source = choice ?? option;
    const { quantity = '1', price = '', compareAt = '', savings = '' } = /** @type {HTMLElement} */ (source).dataset;

    if (choice) {
      this.#fill(option, 'price', price);
      this.#fill(option, 'compare-at', compareAt);
    }

    const savingsLine = this.querySelector('[data-volume-picker-savings]');
    this.#fill(this, 'savings-amount', savings);
    savingsLine?.toggleAttribute('hidden', savings === '');

    const input = this.#quantityInput;
    if (input && input.value !== quantity) {
      input.value = quantity;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const giftText = option.querySelector('[data-volume-picker-gift-text]');
    if (giftText && this.dataset.giftOne) {
      const count = Number(quantity);
      giftText.textContent = `+ ${count} ${count === 1 ? this.dataset.giftOne : this.dataset.giftMany}`;
    }

    if (this.hasAttribute('data-per-item')) this.#renderItems(option, Number(quantity));
  }

  /**
   * @param {HTMLElement} option
   * @param {number} count
   */
  #renderItems(option, count) {
    if (this.#variants.size === 0) return;

    for (const rows of this.querySelectorAll('[data-volume-picker-rows]')) {
      if (!option.contains(rows)) {
        rows.setAttribute('hidden', '');
        rows.querySelectorAll('.volume-picker__item').forEach((row) => row.remove());
      }
    }

    const rows = option.querySelector('[data-volume-picker-rows]');
    if (!rows) return;

    const fallback = this.#currentVariantId;
    while (this.#chosen.length < count) this.#chosen.push(this.#chosen[0] ?? fallback);

    const existing = rows.querySelectorAll('.volume-picker__item');
    existing.forEach((row, index) => {
      if (index >= count) row.remove();
    });
    for (let index = existing.length; index < count; index++) rows.append(this.#buildItem(index, count));
    rows.querySelectorAll('.volume-picker__item').forEach((row, index) => this.#paintItem(row, index, count));
    rows.removeAttribute('hidden');

    this.#updateMixed(count);
  }

  /**
   * @param {number} index
   * @param {number} count
   */
  #buildItem(index, count) {
    const row = document.createElement('span');
    row.className = 'volume-picker__item';

    const number = document.createElement('span');
    number.className = 'volume-picker__item-number';
    row.append(number);

    const thumb = document.createElement('img');
    thumb.className = 'volume-picker__item-thumb';
    thumb.alt = '';
    thumb.width = 32;
    thumb.height = 32;
    row.append(thumb);

    const select = document.createElement('select');
    select.className = 'volume-picker__item-select';
    select.setAttribute('data-volume-picker-item', '');
    for (const variant of this.#variants.values()) {
      const choice = document.createElement('option');
      choice.value = String(variant.id);
      choice.textContent = variant.title;
      choice.disabled = !variant.available;
      select.append(choice);
    }
    select.addEventListener('change', () => {
      const position = Array.prototype.indexOf.call(row.parentElement?.querySelectorAll('.volume-picker__item') ?? [], row);
      this.#chosen[position] = Number(select.value);
      this.#touched.add(position);
      this.#paintItem(row, position, count);
      if (position === 0) {
        this.#selectMainVariant(Number(select.value));
        this.#markSwatch(Number(select.value));
      }
      this.#updateMixed(this.#itemCount);
    });
    row.append(select);
    return row;
  }

  /**
   * @param {Element} row
   * @param {number} index
   * @param {number} count
   */
  #paintItem(row, index, count) {
    const variant = this.#variants.get(this.#chosen[index]) ?? this.#variants.values().next().value;
    if (!variant) return;

    const number = row.querySelector('.volume-picker__item-number');
    if (number) number.textContent = count > 1 ? `No. ${index + 1}` : '';
    number?.toggleAttribute('hidden', count === 1);

    const thumb = /** @type {HTMLImageElement | null} */ (row.querySelector('.volume-picker__item-thumb'));
    if (thumb) {
      thumb.toggleAttribute('hidden', !variant.image);
      if (variant.image && thumb.src !== variant.image) thumb.src = variant.image;
    }

    const select = /** @type {HTMLSelectElement | null} */ (row.querySelector('select'));
    if (select) {
      select.value = String(variant.id);
      select.setAttribute('aria-label', count > 1 ? `Colour for item ${index + 1}` : 'Colour');
    }
  }

  /**
   * A swatch sets the first item and every item line the customer has not changed by hand.
   * @param {number} variantId
   */
  #pickSwatch(variantId) {
    if (!this.#variants.has(variantId)) return;

    const count = this.#itemCount;
    while (this.#chosen.length < count) this.#chosen.push(variantId);
    this.#chosen = this.#chosen.map((id, index) => (index === 0 || !this.#touched.has(index) ? variantId : id));
    this.#touched.delete(0);

    const option = this.#checkedOption;
    if (option) {
      option.querySelectorAll('.volume-picker__item').forEach((row, index) => this.#paintItem(row, index, count));
    }
    this.#updateMixed(count);
    this.#selectMainVariant(variantId);
    this.#markSwatch(variantId);
  }

  /** @param {number} variantId */
  #markSwatch(variantId) {
    const variant = this.#variants.get(variantId);
    this.querySelectorAll('[data-volume-picker-swatch]').forEach((swatch) => {
      swatch.setAttribute('aria-pressed', String(Number(swatch.getAttribute('data-volume-picker-swatch')) === variantId));
    });
    const name = this.querySelector('[data-volume-picker-swatch-name]');
    if (name && variant) name.textContent = variant.option1;
  }

  get #itemCount() {
    return Number(this.#quantityInput?.value) || 1;
  }

  /** @param {number} count */
  #updateMixed(count) {
    const picked = new Set(this.#chosen.slice(0, count));
    this.toggleAttribute('data-mixed', picked.size > 1);
  }

  /**
   * Selects the variant in the product's own (hidden) variant picker so the gallery,
   * the form's variant id and the URL follow the first item.
   * @param {number} variantId
   */
  #selectMainVariant(variantId) {
    const variant = this.#variants.get(variantId);
    if (!variant) return;

    const picker = this.#scope.querySelector('variant-picker');
    const radio = picker?.querySelector(`input[type="radio"][value="${CSS.escape(variant.option1)}"]`);
    if (radio instanceof HTMLInputElement) {
      if (!radio.checked) radio.click();
      return;
    }
    const menu = picker?.querySelector('select');
    if (menu instanceof HTMLSelectElement && menu.value !== variant.option1) {
      menu.value = variant.option1;
      menu.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #onSubmit = (/** @type {SubmitEvent} */ event) => {
    if (!this.hasAttribute('data-mixed') || event.target !== this.#form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addMixed();
  };

  async #addMixed() {
    const count = this.#itemCount;
    /** @type {Map<number, number>} */
    const counts = new Map();
    for (const id of this.#chosen.slice(0, count)) counts.set(id, (counts.get(id) ?? 0) + 1);
    const items = [...counts].map(([id, quantity]) => ({ id, quantity }));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const root = window.Shopify?.routes?.root ?? '/';
    const productForm = this.#form?.closest('product-form-component') ?? this;

    /** @type {any} */
    let events = null;
    try {
      events = await import('@shopify/events');
    } catch {
      events = null;
    }

    const deferred = events?.CartLinesUpdateEvent?.createPromise?.();
    if (events && deferred) {
      productForm.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise,
        })
      );
    }

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((res) => res.json());

      if (!events || !deferred) {
        window.location.href = `${root}cart`;
        return;
      }

      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: productForm.id ? String(productForm.id) : '',
          itemCount: count,
          productId: productForm.dataset?.productId,
          sections: result.sections,
          didError: Boolean(result.status),
        },
      });
      if (result.status) window.alert(result.description || result.message || 'Could not add to cart');
    } catch (error) {
      deferred?.reject?.(error);
      window.location.href = `${root}cart`;
    }
  }

  /**
   * @param {Element} scope
   * @param {string} name
   * @param {string} value
   */
  #fill(scope, name, value) {
    const element = scope.querySelector(`[data-volume-picker-${name}]`);
    if (element) element.textContent = value;
  }
}

if (!customElements.get('volume-picker')) {
  customElements.define('volume-picker', VolumePicker);
}
